import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { PDKOpenInNewTabIcon } from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HOTKEYS } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';

import { isImage, isPdf } from '@/lib/get-file-type';
import { useIsEngineer } from '@/lib/get-user-role';
import { pdkAxios } from '@/lib/pdk-axios';
import type { FileMetadata } from '@/models/types';
import fileDownload from 'js-file-download';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  RotateCwIcon,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import FileNameEditor from './file-name-editor';

const downloadFile = (url: string, filename: string) => {
  // fetch(url, {referrerPolicy: 'no-referrer-when-downgrade'}).then(res => {
  //   console.log(res);
  //   fileDownload(res.data, filename);
  // });

  // we start with
  // https://ever-cars-prod.bucket.vpce-06735a092fbcf596a-9nr2def7.s3.us-east-1.vpce.amazonaws.com/private/c45a5bb7-0f71-4ce7-bc78-0c414c594a86/unnamed%20-%202025-06-13T172238.000.jpg
  // we want https://ever-cars-prod.s3.us-east-1.amazonaws.com/private/c45a5bb7-0f71-4ce7-bc78-0c414c594a86/unnamed%20-%202025-06-13T172238.000.jpg
  const convertedUrl = url.replace(
    'ever-cars-prod.bucket.vpce-06735a092fbcf596a-9nr2def7.s3.us-east-1.vpce.amazonaws.com',
    'ever-cars-prod.s3.us-east-1.amazonaws.com',
  );

  // console.log('downloadFile', convertedUrl);
  pdkAxios
    .get(convertedUrl, {
      responseType: 'blob',
      headers: {
        Authorization: undefined,
      },
    })
    .then((res) => {
      fileDownload(res.data, filename);
    });
};

const DialogImagePdfCarousel = ({
  dealId,
  dealShortId,
  fileToView,
  imageFiles,
  fileToViewIndex,
  setFileToViewIndex,
}: {
  dealId: string;
  dealShortId: string;
  fileToView: FileMetadata | null;
  imageFiles: FileMetadata[];
  fileToViewIndex: number | null;
  setFileToViewIndex: (index: number | null) => void;
}) => {
  const leftArrowDisabled = fileToViewIndex === 0;
  const rightArrowDisabled = fileToViewIndex === imageFiles.length - 1;

  const handleLeftClick = () => {
    if (fileToViewIndex === null) return;
    if (leftArrowDisabled) return;

    setFileToViewIndex(fileToViewIndex - 1);
  };

  const handleRightClick = () => {
    if (fileToViewIndex === null) return;
    if (rightArrowDisabled) return;

    setFileToViewIndex(fileToViewIndex + 1);
  };

  useHotkeys(HOTKEYS.IMAGE_CAROUSEL_GO_TO_PREVIOUS, handleLeftClick);
  useHotkeys(HOTKEYS.IMAGE_CAROUSEL_GO_TO_NEXT, handleRightClick);

  const [fileError, setFileError] = useState(false);
  const [rotations, setRotations] = useState<Record<string, number>>({});

  const imgRotation = rotations[fileToView?.url || ''] || 0;

  useEffect(() => {
    // reset img error when fileToView changes
    if (fileToView?.url) {
      setFileError(false);
    }
  }, [fileToView?.url]);

  const handleRotateClockwise = () => {
    setRotations((prev) => ({
      ...prev,
      [fileToView?.url || '']: ((prev[fileToView?.url || ''] || 0) + 90) % 360,
    }));
  };

  const handleRotateCounterClockwise = () => {
    setRotations((prev) => ({
      ...prev,
      [fileToView?.url || '']:
        ((prev[fileToView?.url || ''] || 0) - 90 + 360) % 360,
    }));
  };

  const handleOpenInNewTab = useCallback(() => {
    if (!fileToView?.url) return;

    const a = document.createElement('a');
    a.href = fileToView?.url;
    a.download = fileToView?.name;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }, [fileToView?.url, fileToView?.name]);

  useHotkeys(HOTKEYS.IMAGE_CAROUSEL_ROTATE_CLOCKWISE, (e) => {
    e.preventDefault();
    e.stopPropagation();

    handleRotateClockwise();
  });

  useHotkeys(HOTKEYS.IMAGE_CAROUSEL_ROTATE_COUNTER_CLOCKWISE, (e) => {
    e.preventDefault();
    e.stopPropagation();

    handleRotateCounterClockwise();
  });

  const isEngineer = useIsEngineer();

  return (
    <Dialog
      open={!!fileToView}
      onOpenChange={() => setFileToViewIndex(null)}
      modal
    >
      <DialogContent
        className="flex flex-col items-center justify-between gap-0 !max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] overflow-hidden"
        onClickClose={() => setFileToViewIndex(null)}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onContextMenu={(e) => e.stopPropagation()}
      >
        <DialogTitle className="relative flex flex-row w-full pr-10 justify-end gap-2 z-50">
          <div className="flex flex-row items-center justify-between gap-2 w-full">
            {fileToView && (
              <FileNameEditor
                dealId={dealId}
                dealShortId={dealShortId}
                file={fileToView}
                className="text-center"
              />
            )}
            <div className="absolute flex flex-row items-center gap-2 right-10">
              <Tooltip delayDuration={500}>
                <TooltipTrigger>
                  <div>
                    <Button
                      variant="outline"
                      disabled={leftArrowDisabled}
                      onClick={handleLeftClick}
                      size="icon"
                    >
                      <ChevronLeftIcon className="size-3.5" />
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="flex flex-row items-center justify-center gap-2 text-xs text-gray-500">
                  Go to previous image
                  <KeyboardKeyIcon>
                    {HOTKEYS.IMAGE_CAROUSEL_GO_TO_PREVIOUS}
                  </KeyboardKeyIcon>
                </TooltipContent>
              </Tooltip>

              <Tooltip delayDuration={500}>
                <TooltipTrigger>
                  <div>
                    <Button
                      variant="outline"
                      disabled={rightArrowDisabled}
                      onClick={handleRightClick}
                      size="icon"
                    >
                      <ChevronRightIcon className="size-3.5" />
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="flex flex-row items-center justify-center gap-2 text-xs text-gray-500">
                  Go to next image
                  <KeyboardKeyIcon>
                    {HOTKEYS.IMAGE_CAROUSEL_GO_TO_NEXT}
                  </KeyboardKeyIcon>
                </TooltipContent>
              </Tooltip>

              {/* TECH DEBT: can't set a hotkey because  `handleOpenInNewTab` requires the latest file but
               * `useHotkeys` doesn't rerender with the latest callback.
               *Thus the way to solve this is to make separate component */}
              <Button
                variant="outline"
                size="icon"
                onClick={handleOpenInNewTab}
              >
                <PDKOpenInNewTabIcon className="size-4 cursor-pointer" />
              </Button>

              <Tooltip delayDuration={500}>
                <TooltipTrigger>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleRotateClockwise}
                  >
                    <RotateCwIcon className="size-4 cursor-pointer" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-row items-center justify-center gap-2 text-xs text-gray-500">
                  Rotate image
                  <KeyboardKeyIcon>
                    {HOTKEYS.IMAGE_CAROUSEL_ROTATE_CLOCKWISE}
                  </KeyboardKeyIcon>
                </TooltipContent>
              </Tooltip>

              {isEngineer && (
                <DownloadIcon
                  onClick={() =>
                    downloadFile(fileToView?.url || '', fileToView?.name || '')
                  }
                />
              )}
            </div>
          </div>
        </DialogTitle>
        <div className="flex flex-col items-center justify-between gap-4 h-[90vh] w-[90vw]">
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-4',
              imgRotation === 0 && 'rotate-0 w-[80vw] h-[80vh]',
              imgRotation === 90 && 'rotate-90 w-[80vh] h-[80vw]',
              imgRotation === 180 && 'rotate-180 w-[80vw] h-[80vh]',
              imgRotation === 270 && 'rotate-270 w-[80vh] h-[80vw]',
            )}
          >
            {fileError ? (
              <div className="flex flex-col items-center justify-center gap-4">
                <p className="text-sm text-gray-500">
                  Image failed to load. Make sure you're on the VPN
                </p>
              </div>
            ) : (
              fileToView &&
              isImage(fileToView) && (
                <img
                  src={fileToView?.url}
                  className={cn('block max-w-[90%] max-h-[90%] object-contain')}
                  draggable={false}
                  onError={() => setFileError(true)}
                  onLoad={() => setFileError(false)}
                />
              )
            )}
            {fileError ? (
              <div className="flex flex-col items-center justify-center gap-4">
                <p className="text-sm text-gray-500">
                  Pdf failed to load. Make sure you're on the VPN
                </p>
              </div>
            ) : (
              fileToView &&
              isPdf(fileToView) && (
                <embed
                  src={fileToView?.url}
                  width="95%"
                  height="95%"
                  onError={() => setFileError(true)}
                  onLoad={() => setFileError(false)}
                />
              )
            )}
          </div>
        </div>
        <DialogFooter></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DialogImagePdfCarousel;
